import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

interface Profile {
  id: string;
  name: string;
  login: string;
  avatar: string | null;
  organization_id: string | null;
  is_blocked: boolean;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  role: string | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, name: string, login: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Слушатель auth-событий — только синхронные обновления стейта
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      // ВАЖНО: не дергаем загрузку профиля на TOKEN_REFRESHED,
      // иначе при возврате на вкладку получится ощущение "перезагрузки" из-за глобального loading.
      if (session?.user) {
        if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
          setTimeout(() => {
            void loadProfile(session.user.id, session.user.email ?? null, { blockUI: false });
          }, 0);
        }
      } else {
        setProfile(null);
        setRole(null);
        setLoading(false);
      }
    });

    // Затем подтягиваем уже существующую сессию
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        void loadProfile(session.user.id, session.user.email ?? null, { blockUI: true });
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (
    userId: string,
    fallbackEmail?: string | null,
    options: { blockUI?: boolean } = {}
  ) => {
    const blockUI = options.blockUI === true;

    try {
      if (blockUI) setLoading(true);

      const [{ data: profileData, error: profileError }, { data: roleData, error: roleError }] =
        await Promise.all([
          supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
          supabase.from('user_roles').select('role').eq('user_id', userId).maybeSingle(),
        ]);

      if (profileError) console.warn('Profile load error:', profileError);
      if (roleError) console.warn('Role load error:', roleError);

      const resolvedRole = roleData?.role ?? 'user';
      setRole(resolvedRole);

      if (profileData) {
        setProfile(profileData);
      } else {
        // Не ломаем UI, даже если профиль не создан/недоступен
        const fallback = fallbackEmail ?? 'Пользователь';
        setProfile({
          id: userId,
          name: fallback,
          login: fallback,
          avatar: null,
          organization_id: null,
          is_blocked: false,
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setRole('user');
      setProfile((prev) =>
        prev ?? {
          id: userId,
          name: fallbackEmail ?? 'Пользователь',
          login: fallbackEmail ?? 'Пользователь',
          avatar: null,
          organization_id: null,
          is_blocked: false,
        }
      );
    } finally {
      if (blockUI) setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    
    if (!error && data.user && data.session) {
      // Get user profile name for logging
      const { data: profileData } = await supabase
        .from('profiles')
        .select('name, is_blocked, organization_id')
        .eq('id', data.user.id)
        .maybeSingle();
      
      // Log successful login - now user is authenticated so RLS should allow INSERT
      const logResult = await supabase.from('user_activity_logs').insert({
        user_id: data.user.id,
        user_email: data.user.email,
        user_name: profileData?.name || data.user.email,
        action_type: 'auth_login',
        action_description: `Вход в систему: ${data.user.email}`,
        metadata: { login_method: 'password' },
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      });
      
      if (logResult.error) {
        console.error('Failed to log login activity:', logResult.error);
      }
      
      if (profileData?.is_blocked) {
        await supabase.auth.signOut();
        return { error: { message: 'Ваш аккаунт заблокирован. Обратитесь к администратору для получения подписки.' } };
      }

      // Check if user has active subscription (personal or organization)
      const { data: userSub } = await supabase
        .from('subscriptions')
        .select('id')
        .eq('user_id', data.user.id)
        .eq('is_active', true)
        .gte('ends_at', new Date().toISOString())
        .limit(1)
        .maybeSingle();

      let hasSubscription = !!userSub;

      // If no personal subscription, check organization subscription
      if (!hasSubscription && profileData?.organization_id) {
        const { data: orgSub } = await supabase
          .from('subscriptions')
          .select('id')
          .eq('organization_id', profileData.organization_id)
          .eq('is_active', true)
          .gte('ends_at', new Date().toISOString())
          .limit(1)
          .maybeSingle();
        
        hasSubscription = !!orgSub;
      }

      // Block user if no subscription and not admin
      if (!hasSubscription) {
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', data.user.id)
          .maybeSingle();

        // Admins and moderators can login without subscription
        if (roleData?.role !== 'admin' && roleData?.role !== 'moderator') {
          await supabase.auth.signOut();
          return { error: { message: 'У вас нет активной подписки. Обратитесь к администратору.' } };
        }
      }
    }
    
    return { error };
  };

  const signUp = async (email: string, password: string, name: string, login: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl },
    });

    if (!error && data.user) {
      await supabase.from('profiles').insert({
        id: data.user.id,
        name,
        login,
        avatar: null, // Не генерируем случайную аватарку
      });

      await supabase.from('user_roles').insert({
        user_id: data.user.id,
        role: 'user',
      });

      await supabase.from('user_permissions').insert({
        user_id: data.user.id,
        apps: ['whatsapp'],
        sites: ['tyrplus'],
      });
    }

    return { error };
  };

  const signOut = async () => {
    // Log logout before signing out (while still authenticated)
    if (user) {
      const logResult = await supabase.from('user_activity_logs').insert({
        user_id: user.id,
        user_email: user.email,
        user_name: profile?.name || user.email,
        action_type: 'auth_logout',
        action_description: `Выход из системы: ${user.email}`,
        metadata: {},
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      });
      
      if (logResult.error) {
        console.error('Failed to log logout activity:', logResult.error);
      }
    }
    
    await supabase.auth.signOut();
    setProfile(null);
    setRole(null);
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return;

    await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);

    setProfile((prev) => prev ? { ...prev, ...updates } : null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        role,
        session,
        loading,
        signIn,
        signUp,
        signOut,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};